import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * A product row's "⋯": «Medios», «Descripción» and the X, folded into one
 * control so the row fits.
 *
 * The X DEACTIVATES a saved product (its leads and media survive), and the
 * menu says so. A row that was never saved has nothing to deactivate, so it
 * offers «Quitar» — calling that "desactivar" would describe something that
 * never existed. Media and description hang off the product's id, so both wait
 * until the row has been created — which happens as soon as it has a name.
 *
 * «Mover a…» lists the other groups (the root and each category); the product
 * lands at the end of the one picked. A school row also offers switching its
 * calendar, by cycle ↔ with its own dates.
 */
export function ProductRowMenu({
  productName,
  saved,
  disabled,
  onOpenMedia,
  onOpenDescription,
  onRemove,
  moveTargets = [],
  onMove,
  scheduleAction,
}: {
  productName: string;
  /** Has a DB id: media and description can attach, and removal means deactivation. */
  saved: boolean;
  disabled?: boolean;
  onOpenMedia: () => void;
  onOpenDescription: () => void;
  onRemove: () => void;
  /** The groups this product can move to — never the one it is in. */
  moveTargets?: Array<{ id: string | null; label: string }>;
  onMove?: (categoryId: string | null) => void;
  /** «Cambiar a fechas propias» / «Cambiar a por ciclo», on a school row. */
  scheduleAction?: { label: string; onSelect: () => void };
}): React.JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Opciones de ${productName || "este programa"}`}
          disabled={disabled}
          className="flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:bg-ink-100 hover:text-ink-600 focus:outline-none disabled:pointer-events-none disabled:opacity-30"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem disabled={!saved} onSelect={onOpenMedia}>
          Medios…
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!saved} onSelect={onOpenDescription}>
          Descripción…
        </DropdownMenuItem>
        {saved && moveTargets.length > 0 && onMove && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Mover a…</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {moveTargets.map((target) => (
                <DropdownMenuItem key={target.id ?? "root"} onSelect={() => onMove(target.id)}>
                  {target.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {scheduleAction && (
          <DropdownMenuItem onSelect={scheduleAction.onSelect}>{scheduleAction.label}</DropdownMenuItem>
        )}
        {!saved && (
          <DropdownMenuLabel className="max-w-[220px] text-[11px] font-normal text-ink-400">
            Escribe el nombre para agregar medios y descripción
          </DropdownMenuLabel>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onRemove}>{saved ? "Desactivar" : "Quitar"}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
